/**
 * Utility functions for managing report permissions
 * 
 * NOTE: This is a basic client-side implementation.
 * For production use, permissions should be validated server-side
 * using Row Level Security (RLS) policies in the database.
 */

interface ReportWithAuthor {
  createdBy?: string;
}

interface User {
  id: string;
  role: 'admin' | 'user' | 'responsable' | 'pasteur';
}

/**
 * Checks if a user can edit a report
 * @param report - The report to check
 * @param currentUser - The current user
 * @returns true if the user can edit the report, false otherwise
 */
export function canEditReport(
  report: ReportWithAuthor,
  currentUser: User | null
): boolean {
  if (!currentUser) return false;
  
  // Admins and pastors can edit everything
  if (currentUser.role === 'admin' || currentUser.role === 'pasteur') {
    return true;
  }
  
  // Responsables can edit their own reports
  if (currentUser.role === 'responsable') {
    return report.createdBy === currentUser.id;
  }
  
  // Regular users can only edit their own reports
  return report.createdBy === currentUser.id;
}

/**
 * Checks if a user can delete a report
 * @param report - The report to check
 * @param currentUser - The current user
 * @returns true if the user can delete the report, false otherwise
 */
export function canDeleteReport(
  report: ReportWithAuthor,
  currentUser: User | null
): boolean {
  if (!currentUser) return false;

  // Admins and pastors can delete any report
  if (currentUser.role === 'admin' || currentUser.role === 'pasteur') {
    return true;
  }

  // The author can delete their own report
  return report.createdBy === currentUser.id;
}

/**
 * Gets a user-friendly message explaining why edit is not allowed
 * @param report - The report to check
 * @param currentUser - The current user
 * @returns A message explaining the permission restriction
 */
export function getEditPermissionMessage(
  report: ReportWithAuthor,
  currentUser: User | null
): string {
  if (!currentUser) {
    return 'Vous devez être connecté pour modifier ce rapport';
  }
  
  if (currentUser.role === 'admin' || currentUser.role === 'pasteur') {
    return ''; // No restriction
  }
  
  if (report.createdBy === currentUser.id) {
    return ''; // Can edit own report
  }
  
  return 'Seul l\'auteur du rapport ou un administrateur peut le modifier';
}
